import {inject, Injectable} from '@angular/core';
import {DashboardSummaryRepresentation} from './representations/dashboard-summary-representation';
import {HttpClient, HttpParams} from '@angular/common/http';
import {DashboardQueryRepresentation} from './representations/dashboard-query-representation';
import {filter, forkJoin, map, mergeMap, Observable, of, Subject, switchMap, take, tap, throwError} from 'rxjs';
import {DashboardDetailRepresentation} from './representations/dashboard-detail-representation';
import {SelectedAdviserService} from '../../core/services/selected-adviser.service';
import {environment} from '../../../environments/environment';
import {UserDetails} from '../../core/models/user-details';
import {Widget} from '../../features/widgets/models/widget';
import {WidgetTemplate} from '../../features/widgets/models/widget-template';
import {WidgetPositionRepresentation} from './representations/widget-position-representation';
import {WidgetTable} from '../../features/widgets/models/widget-table';

@Injectable({
  providedIn: 'root',
})
export class DashboardPersistenceService {

  private readonly WIDGET_DATASET_TTL_MS = 30 * 60 * 60;  // 30 Minutes

  private http = inject(HttpClient);
  private selectedAdviserService = inject(SelectedAdviserService);

  private readonly dashboardApiUrl = `${environment.connectCrmApiUrl}/v1/dashboards`;
  private readonly widgetsApiUrl = `${environment.connectCrmApiUrl}/v1/widget-templates`;

  private widgetTemplates: WidgetTemplate[];

  /** Client-side caching **/
  // Map structure: <dashboardName, dashboardSummary>
  private dashboardSummariesMap: Map<string, DashboardSummaryRepresentation> = new Map<string, DashboardSummaryRepresentation>();

  // Map structure: <dashboardKey, dashboardDetails>
  private dashboardDetailsMap: Map<string, DashboardDetailRepresentation> = new Map<string, DashboardDetailRepresentation>();

  // Map structure: <dashboardKey, Map<widgetKey:tableKey, {timestamp (Seconds from epoch), dataset}>>
  private widgetsMap: Map<string, Map<string, {timestamp: number, dataset: object}>> = new Map<string, Map<string, {timestamp: number; dataset: object}>>();

  private widgetPositionSaveResults$ = new Subject<{
    dashboardKey: string;
    widgetKey: string;
    error?: unknown;
  }>();

  // Get all available widget templates from the backed
  getAvailableWidgetTemplates(): Observable<WidgetTemplate[]> {
    // If the templates have ceched before, return the cache
    if (this.widgetTemplates) {
      return of(this.widgetTemplates);
    }

    /**
     * Retrieve widget templates from backend
     */
    return this.selectedAdviserService.selectedRole$.pipe(
      // Do not continue until signal have been properly initiated
      filter((userDetails: UserDetails)=> !!userDetails.subjectId),
      switchMap(() =>  this.http.get<WidgetTemplate[]>(this.widgetsApiUrl)),
      // Write widgets to the client-side cache
      tap((widgetTemplates: WidgetTemplate[]) => {
        this.widgetTemplates = widgetTemplates;
      })
    );
  }

  /**
   * Get all the widgets configured for a dashboard.
   **/
  getDashboardWidgets(dashboardName: string): Observable<Widget[]>  {
    return this.selectedAdviserService.selectedRole$.pipe(
      filter((userDetails: UserDetails)=> !!userDetails.subjectId),
      // Find the dashboard summary from the dashboard name
      switchMap((userDetails: UserDetails) => this.getDashboard(userDetails, dashboardName)),
      // Get the details for the correct dashboard ke, with the configured widgets
      switchMap((dashboard: DashboardSummaryRepresentation) => this.retrieveDashboardDetails(this.selectedAdviserService.selectedRole, dashboard.key)),
      // Save the widgets to the client-side cache
      tap((dashboardRepresentation: DashboardDetailRepresentation) => {
        // Check if there is already an entry for the dashboard, and if not, create it.
        if (!this.widgetsMap.has(dashboardRepresentation.key)) {
          this.widgetsMap.set(dashboardRepresentation.key, new Map<string, {timestamp: number; dataset: object}>());
        }
        const map = this.widgetsMap.get(dashboardRepresentation.key);
        // Add the widget datasets to the client-side cache
        dashboardRepresentation.widgets.forEach((widget: Widget) => {
          widget.tables?.forEach((table: WidgetTable) => {
            if (table.dataset) {
              map?.set(`${widget.widgetKey}:${table.key}`, {timestamp: Date.now(), dataset: table.dataset});
            }
          })
        })
      }),
      // return only the widgets
      map((dashboardRepresentation: DashboardDetailRepresentation) =>
        dashboardRepresentation.widgets.map(widget => ({
          ...widget,
          tables: widget.tables?.sort((a, b) => a.orderIndex - b.orderIndex)
        }))
      )
    )
  }

  /**
   * Get the dataset for a specific widget on a specific daahboard.
   */
  getWidgetDataset(dashboardName: string, widgetKey: string, tableKey: string): Observable<[]> {
    return this.selectedAdviserService.selectedRole$.pipe(
      filter((userDetails: UserDetails)=> !!userDetails.subjectId),
      // Get the dashboard from the dashboard name. This will almost always come from the cache, so no additional overhead.
      switchMap((userDetails: UserDetails) => this.getDashboard(userDetails, dashboardName)),
      switchMap((dashboard: DashboardSummaryRepresentation) => {
        // Get the cached dataset (it will be undefined if it hasn't been cahced yet)
        const cachedDataset = this.getWidgetDatasetFromCache(dashboard.key, widgetKey, tableKey);
        if (cachedDataset) {
          return of(cachedDataset as []);
        }

        const retrieveDataset = () => {
          const params = new HttpParams()
            .set('contextKey', this.selectedAdviserService.selectedRole.contextKey)
            .set('fsp', this.selectedAdviserService.selectedRole.fsp);
          return this.http.get<[]>(`${this.dashboardApiUrl}/${dashboard.key}/widgets/${widgetKey}/tables/${tableKey}/dataset`, {params: params}).pipe(
            tap((dataset: [])=> {
              this.cacheDataset(dashboard.key, widgetKey, tableKey, dataset);
            })
          );
        };

        if (this.isWidgetMissingFromCachedDashboard(dashboard.key, widgetKey)) {
          return this.waitForWidgetPositionSave(dashboard.key, widgetKey).pipe(
            switchMap(() => retrieveDataset())
          );
        }

        return retrieveDataset();
      })
    );
  }

  /**
   * Remove a specific item from a widget table
   */
  removeWidgetItem(dashboardName: string, widgetKey: string, widgetTableKey: string, widgetItemKey: string): Observable<never> {
    return this.getDashboard(this.selectedAdviserService.selectedRole, dashboardName).pipe(
      switchMap((dashboard: DashboardSummaryRepresentation) => {
        const url = `${this.dashboardApiUrl}/${dashboard.key}/widgets/${widgetKey}/tables/${widgetTableKey}/dataset`;
        const widgetItemStatusRepresentation = {
          contextKey: this.selectedAdviserService.selectedRole.contextKey,
          fsp: this.selectedAdviserService.selectedRole.fsp,
          itemKey: widgetItemKey,
          status: 'REMOVED'
        };
        return this.http.put<never>(url, widgetItemStatusRepresentation);
      })
    );
  }

  /**
   * Save a widget's position on a specific dashboard to the backend.
   */
  saveWidgetPosition(dashboardName: string, widget: Widget): Observable<never> {
    // Get the Dashboard from the dashboard name
    return this.getDashboard(this.selectedAdviserService.selectedRole, dashboardName).pipe(
      // Note - using mergeMap, because we don't want save operations that are already in progress to abort when another one comes in
      mergeMap((dashboard: DashboardSummaryRepresentation) => {
        const savedWidget = {
          ...widget,
          gridAddress: {
            topLeft: {...widget.gridAddress.topLeft},
            bottomRight: {...widget.gridAddress.bottomRight}
          }
        };
        const widgetPositionRepresentation = {
          widgetName: savedWidget.widgetName,
          widgetKey: savedWidget.widgetKey,
          gridAddress: savedWidget.gridAddress
        } as WidgetPositionRepresentation;

        const url = `${this.dashboardApiUrl}/${dashboard.key}/widgets/${savedWidget.widgetKey}`;
        const params = new HttpParams()
          .set('contextKey', this.selectedAdviserService.selectedRole.contextKey)
          .set('fsp', this.selectedAdviserService.selectedRole.fsp);
        // Save the widget. Don't bother returning anything beyond success/fail for now.
        return this.http.put<never>(url, widgetPositionRepresentation, {params: params}).pipe(
          tap({
            next: () => {
              this.updateCachedWidgetPosition(dashboard.key, savedWidget);
              this.widgetPositionSaveResults$.next({
                dashboardKey: dashboard.key,
                widgetKey: savedWidget.widgetKey
              });
            },
            error: (error: unknown) => {
              this.widgetPositionSaveResults$.next({
                dashboardKey: dashboard.key,
                widgetKey: savedWidget.widgetKey,
                error
              });
            }
          })
        );
      })
    );
  }

  /**
   * Remove a widget from a specific dashboard
   */
  removeWidget(dashboardName: string, widget: Widget): Observable<never> {
    return this.getDashboard(this.selectedAdviserService.selectedRole, dashboardName).pipe(
      mergeMap((dashboard: DashboardSummaryRepresentation)=> {
        const url = `${this.dashboardApiUrl}/${dashboard.key}/widgets/${widget.widgetKey}`;
        const params = new HttpParams()
          .set('contextKey', this.selectedAdviserService.selectedRole.contextKey)
          .set('fsp', this.selectedAdviserService.selectedRole.fsp);
        return this.http.delete<never>(url, {params: params}).pipe(
          tap(() => this.removeCachedWidget(dashboard.key, widget.widgetKey))
        );
      })
    )
  }

  /**
   * Save widget selection - Update the widgets on the dashboard and remove any widgets that should be removed.
   * @param dashboardName
   * @param enabledWidgets
   * @param removedWidgets
   */
  saveWidgetSelection(dashboardName: string, enabledWidgets: Widget[], removedWidgets: Widget[]): Observable<never[]> {
    // Add / Update widgets that are selected
    const apiCalls: Observable<never>[] = [];//enabledWidgets.map((widget: Widget) => this.saveWidgetPosition(dashboardName, widget));
    // Remove widgets.
    apiCalls.push(...removedWidgets.map((widget: Widget) => this.removeWidget(dashboardName, widget)));

    // Make all calls in parallel to save time.
    return forkJoin(apiCalls);
  }

  /**
   * Retrieve a widget dataset from the cache. Return undefined if it doesn't exist.
   */
  private getWidgetDatasetFromCache(dashboardKey: string, widgetKey: string, tableKey: string): object | undefined {
    // Get the Map for the dashboard
    const cachedDashboard = this.widgetsMap.get(dashboardKey);
    const cacheKey = `${widgetKey}:${tableKey}`;
    if (cachedDashboard) {
      // Get the cache for the widget

      const widgetCache = cachedDashboard.get(cacheKey);
      if (widgetCache) {
        // Check if the TTL has expired, and if so - delete it and return undefined.
        const now = Date.now();
        if (now - widgetCache?.timestamp > this.WIDGET_DATASET_TTL_MS) {
          cachedDashboard.delete(cacheKey);
          return undefined;
        }
      }
      return widgetCache?.dataset;
    }
    return undefined;
  }

  /**
   * Cache a widget dataset
   */
  private cacheDataset(dashboardKey: string, widgetKey: string, tableKey: string, dataset: []): void {
    // Get the Map for the dashboard.
    let cachedDashboard = this.widgetsMap.get(dashboardKey);
    // If the Map for the dashboard doesn't exist, create it.
    if (!cachedDashboard) {
      cachedDashboard = new Map<string, {timestamp: number; dataset: object}>();
      this.widgetsMap.set(dashboardKey, cachedDashboard);
    }
    cachedDashboard.set(`${widgetKey}:${tableKey}`, {timestamp: Date.now(), dataset: dataset});
  }

  private updateCachedWidgetPosition(dashboardKey: string, widget: Widget): void {
    const cachedDashboard = this.dashboardDetailsMap.get(dashboardKey);
    if (!cachedDashboard) {
      return;
    }

    const cachedWidget = cachedDashboard.widgets.find(
      (candidate: Widget) => candidate.widgetKey === widget.widgetKey
    );

    if (cachedWidget) {
      cachedWidget.gridAddress = {
        topLeft: {...widget.gridAddress.topLeft},
        bottomRight: {...widget.gridAddress.bottomRight}
      };
      return;
    }

    cachedDashboard.widgets.push({
      ...widget,
      gridAddress: {
        topLeft: {...widget.gridAddress.topLeft},
        bottomRight: {...widget.gridAddress.bottomRight}
      }
    });
  }

  private removeCachedWidget(dashboardKey: string, widgetKey: string): void {
    const cachedDashboard = this.dashboardDetailsMap.get(dashboardKey);
    if (!cachedDashboard) {
      return;
    }

    cachedDashboard.widgets = cachedDashboard.widgets.filter(
      (widget: Widget) => widget.widgetKey !== widgetKey
    );
  }

  private isWidgetMissingFromCachedDashboard(dashboardKey: string, widgetKey: string): boolean {
    const cachedDashboard = this.dashboardDetailsMap.get(dashboardKey);
    return !!cachedDashboard
      && !cachedDashboard.widgets.some((widget: Widget) => widget.widgetKey === widgetKey);
  }

  private waitForWidgetPositionSave(dashboardKey: string, widgetKey: string): Observable<void> {
    return this.widgetPositionSaveResults$.pipe(
      filter((result) =>
        result.dashboardKey === dashboardKey && result.widgetKey === widgetKey
      ),
      take(1),
      mergeMap((result) =>
        result.error
          ? throwError(() => result.error)
          : of(undefined)
      )
    );
  }

  /**
   * Get a dashboard from a dashboard name.
   */
  private getDashboard(userDetails: UserDetails, dashboardName: string): Observable<DashboardSummaryRepresentation> {
    // Check the cache, and return the cached value if it exists
    const dashboard = this.dashboardSummariesMap.get(dashboardName);
    if (dashboard) {
      return of(dashboard);
    }

    // Search for all dashboards with the name (There is meant to be only one, but since the dashboardName is an optional parameter, the
    // API returns a list.
    const query = {
      fsp: userDetails.fsp,
      contextKey: userDetails.contextKey,
      dashboardName: dashboardName
    } as DashboardQueryRepresentation;

    return this.http.post<DashboardSummaryRepresentation[]>(`${this.dashboardApiUrl}/query`,query).pipe(
      // Cache the dashboards.
      tap((dashboards: DashboardSummaryRepresentation[]) => {
        dashboards.forEach((dashboard: DashboardSummaryRepresentation) => {
          this.dashboardSummariesMap.set(dashboardName, dashboard);
        });
      }),
      // Return the first one (There should be only one).
      map((dashboards: DashboardSummaryRepresentation[]) => dashboards[0]),
    );
  }

  /**
   * Retrieve the details for a dashboard using its key. Details will include the widgets, but not the widget datasets.
   */
  private retrieveDashboardDetails(userDetails: UserDetails, dashboardKey: string): Observable<DashboardDetailRepresentation> {
    // Check the cache, and return the cached value if it exists.
    const dashboardDetails = this.dashboardDetailsMap.get(dashboardKey);
    if (dashboardDetails) {
      return of(dashboardDetails);
    }

    const params = new HttpParams()
      .set('contextKey', userDetails.contextKey)
      .set('fsp', userDetails.fsp);
    return this.http.get<DashboardDetailRepresentation>(`${this.dashboardApiUrl}/${dashboardKey}`, {params: params}).pipe(
      // Cache the dashboards.
      tap((dashboardDetails: DashboardDetailRepresentation) => {
        this.dashboardDetailsMap.set(dashboardDetails.key, dashboardDetails);
      })
    );
  }

}
